import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { LogOut, LayoutDashboard, Package, Truck, ShoppingBag, Film, Star, MessageSquare, Mail, FileText, Layers, Image as ImageIcon, Users, Send } from "lucide-react";
import { adminMe, adminLogin, adminLogout } from "../lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Which nav links each role can see
const ROLE_PERMISSIONS = {
  super_admin: ["dashboard", "products", "orders", "media", "envois", "library", "shipping", "reviews", "messages", "newsletter", "content", "categories", "team"],
  catalog_manager: ["dashboard", "products", "media", "envois", "library", "reviews", "messages", "content", "categories"],
  order_preparer: ["dashboard", "orders", "shipping"],
  logistics: ["dashboard", "orders", "shipping"],
  accountant: ["dashboard", "orders"],
};

const AdminLayout = ({ children, title, subtitle }) => {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [keyInput, setKeyInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [role, setRole] = useState("super_admin");
  const navigate = useNavigate();
  const location = useLocation();

  // SEC-001: check session via HttpOnly cookie (no key ever stored in JS)
  useEffect(() => {
    adminMe()
      .then(async () => {
        setAuthed(true);
        try {
          const { data } = await axios.get(`${API}/admin/whoami`, { withCredentials: true });
          setRole(data.role || "super_admin");
        } catch { /* stay super_admin */ }
      })
      .catch(() => setAuthed(false))
      .finally(() => setChecking(false));
  }, []);

  const can = (mod) => (ROLE_PERMISSIONS[role] || ROLE_PERMISSIONS.super_admin).includes(mod);

  const tryLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await adminLogin(keyInput);
      setAuthed(true);
      setKeyInput("");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) setError("Trop de tentatives. Réessayez dans 15 minutes.");
      else setError("Clé d'accès incorrecte");
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try { await adminLogout(); } catch (_) { /* ignore */ }
    setAuthed(false);
    setKeyInput("");
    navigate("/admin");
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <p className="label-caps text-white/50">Vérification…</p>
      </div>
    );
  }

  if (!authed) {
    // Redirect to the new pro login screen. Fall back to inline key form as safety.
    if (typeof window !== "undefined" && !window.location.pathname.startsWith("/admin/login")) {
      navigate("/admin/login");
    }
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center">
          <img src="/logo-maison-douba.png" alt="MAISON DOUBA" className="h-24 mx-auto mb-8 object-contain" />
          <p className="label-caps text-[color:var(--gold)] mb-4">Espace administrateur</p>
          <h1 className="font-serif text-4xl text-white mb-4">Redirection…</h1>
          <Link to="/admin/login" className="btn-outline">Se connecter</Link>
        </div>
      </div>
    );
  }

  const isActive = (path) => location.pathname === path || (path === "/admin" && location.pathname === "/admin");

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/10 bg-[#080808] sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-5 flex items-center justify-between gap-6">
          <Link to="/admin" className="flex items-center gap-4">
            <img src="/logo-maison-douba.png" alt="MAISON DOUBA" className="h-11 object-contain" />
            <div className="hidden md:block">
              <p className="label-caps text-[color:var(--gold)] text-[10px]">Administration</p>
              <p className="font-serif text-white text-sm">{title || "Tableau de bord"}</p>
            </div>
          </Link>

          <nav className="flex items-center gap-2 md:gap-6 label-caps text-[10px]">
            <Link
              to="/admin"
              data-testid="admin-nav-hub"
              className={`px-3 py-2 transition-colors flex items-center gap-2 ${isActive("/admin") ? "text-[color:var(--gold)]" : "text-white/60 hover:text-white"}`}
            >
              <LayoutDashboard size={12} /> <span className="hidden md:inline">Accueil</span>
            </Link>
            {can("products") && <Link
              to="/admin/products"
              data-testid="admin-nav-products"
              className={`px-3 py-2 transition-colors flex items-center gap-2 ${isActive("/admin/products") ? "text-[color:var(--gold)]" : "text-white/60 hover:text-white"}`}
            >
              <Package size={12} /> <span className="hidden md:inline">Produits</span>
            </Link>}
            {can("orders") && <Link
              to="/admin/orders"
              data-testid="admin-nav-orders"
              className={`px-3 py-2 transition-colors flex items-center gap-2 ${isActive("/admin/orders") ? "text-[color:var(--gold)]" : "text-white/60 hover:text-white"}`}
            >
              <ShoppingBag size={12} /> <span className="hidden md:inline">Commandes</span>
            </Link>}
            {can("media") && <Link
              to="/admin/media"
              data-testid="admin-nav-media"
              className={`px-3 py-2 transition-colors flex items-center gap-2 ${isActive("/admin/media") ? "text-[color:var(--gold)]" : "text-white/60 hover:text-white"}`}
            >
              <Film size={12} /> <span className="hidden md:inline">Coulisses</span>
            </Link>}
            {can("envois") && <Link
              to="/admin/envois"
              data-testid="admin-nav-envois"
              className={`px-3 py-2 transition-colors flex items-center gap-2 ${isActive("/admin/envois") ? "text-[color:var(--gold)]" : "text-white/60 hover:text-white"}`}
            >
              <Send size={12} /> <span className="hidden md:inline">Envois</span>
            </Link>}
            {can("library") && <Link
              to="/admin/library"
              data-testid="admin-nav-library"
              className={`px-3 py-2 transition-colors flex items-center gap-2 ${isActive("/admin/library") ? "text-[color:var(--gold)]" : "text-white/60 hover:text-white"}`}
            >
              <ImageIcon size={12} /> <span className="hidden md:inline">Médiathèque</span>
            </Link>}
            {can("shipping") && <Link
              to="/admin/shipping"
              data-testid="admin-nav-shipping"
              className={`px-3 py-2 transition-colors flex items-center gap-2 ${isActive("/admin/shipping") ? "text-[color:var(--gold)]" : "text-white/60 hover:text-white"}`}
            >
              <Truck size={12} /> <span className="hidden md:inline">Livraison</span>
            </Link>}
            {can("reviews") && <Link
              to="/admin/reviews"
              data-testid="admin-nav-reviews"
              className={`px-3 py-2 transition-colors flex items-center gap-2 ${isActive("/admin/reviews") ? "text-[color:var(--gold)]" : "text-white/60 hover:text-white"}`}
            >
              <Star size={12} /> <span className="hidden md:inline">Avis</span>
            </Link>}
            {can("messages") && <Link
              to="/admin/messages"
              data-testid="admin-nav-messages"
              className={`px-3 py-2 transition-colors flex items-center gap-2 ${isActive("/admin/messages") ? "text-[color:var(--gold)]" : "text-white/60 hover:text-white"}`}
            >
              <MessageSquare size={12} /> <span className="hidden lg:inline">Messages</span>
            </Link>}
            {can("newsletter") && <Link
              to="/admin/newsletter"
              data-testid="admin-nav-newsletter"
              className={`px-3 py-2 transition-colors flex items-center gap-2 ${isActive("/admin/newsletter") ? "text-[color:var(--gold)]" : "text-white/60 hover:text-white"}`}
            >
              <Mail size={12} /> <span className="hidden lg:inline">Newsletter</span>
            </Link>}
            {can("content") && <Link
              to="/admin/content"
              data-testid="admin-nav-content"
              className={`px-3 py-2 transition-colors flex items-center gap-2 ${isActive("/admin/content") ? "text-[color:var(--gold)]" : "text-white/60 hover:text-white"}`}
            >
              <FileText size={12} /> <span className="hidden lg:inline">Contenu</span>
            </Link>}
            {can("categories") && <Link
              to="/admin/categories"
              data-testid="admin-nav-categories"
              className={`px-3 py-2 transition-colors flex items-center gap-2 ${isActive("/admin/categories") ? "text-[color:var(--gold)]" : "text-white/60 hover:text-white"}`}
            >
              <Layers size={12} /> <span className="hidden lg:inline">Catégories</span>
            </Link>}
            {can("team") && <Link
              to="/admin/team"
              data-testid="admin-nav-team"
              className={`px-3 py-2 transition-colors flex items-center gap-2 ${isActive("/admin/team") ? "text-[color:var(--gold)]" : "text-white/60 hover:text-white"}`}
            >
              <Users size={12} /> <span className="hidden lg:inline">Équipe</span>
            </Link>}
            <div className="w-px h-4 bg-white/10 mx-2" />
            <Link to="/" className="text-white/50 hover:text-white hidden md:inline">Voir le site</Link>
            <button onClick={logout} data-testid="admin-logout" className="text-white/50 hover:text-white flex items-center gap-2">
              <LogOut size={12} /> <span className="hidden md:inline">Sortir</span>
            </button>
          </nav>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-12 py-16">
        {(title || subtitle) && (
          <div className="mb-14">
            <p className="label-caps text-[color:var(--gold)] mb-4">Administration</p>
            {title && <h1 className="font-serif text-4xl md:text-5xl text-white mb-4">{title}</h1>}
            {subtitle && <p className="text-white/50 font-light max-w-2xl">{subtitle}</p>}
          </div>
        )}
        {children}
      </main>
    </div>
  );
};

export default AdminLayout;
